<script setup lang="ts">
const queryClient = useQueryClient();
await queryClient.ensureQueryData(contactPageQueryOptions());
const { entrance, location } = useContactPage();

useSchemaOrg([
  defineLocalBusiness({
    '@type': ['LocalBusiness', 'DrivingSchool'],
    'name': 'Kronborgs Køreskole',
    'telephone': '+4540779579',
    'email': 'claus@c-kronborg.dk',
    'address': {
      streetAddress: 'Møllestien 1',
      addressLocality: 'Gistrup',
      postalCode: '9260',
      addressCountry: 'DK',
    },
    'geo': {
      latitude: 57.0218,
      longitude: 9.9958,
    },
    'foundingDate': '1998',
    'priceRange': '$$',
    'openingHoursSpecification': {
      dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
    },
  }),
]);
</script>

<template>
  <div class="min-h-screen flex flex-col bg-[var(--color-cream)]">
    <Header />

    <main
      id="main"
      class="flex-1"
    >
      <slot />
    </main>

    <footer class="bg-[var(--color-charcoal)] text-white relative">
      <!-- Decorative top edge -->
      <div class="absolute -top-px left-0 right-0 h-px bg-gradient-to-r from-transparent via-[var(--color-peach)]/30 to-transparent" />
      <div class="max-w-6xl mx-auto px-6 py-16">
        <div class="grid grid-cols-1 md:grid-cols-3 gap-12">
          <!-- Brand -->
          <div class="flex flex-col items-center md:items-start">
            <Logo
              height="48px"
              dark
            />
            <h4
              class="pt-8 font-semibold"
            >
              Kronborgs Køreskole <br> v/ Claus Kronborg
            </h4>
            <div class="flex flex-col items-center md:items-start pt-4 text-sm text-neutral-400">
              <p>CVR: 26306612</p>
              <p>&copy; Kronborgs Køreskole v/ Claus Kronborg {{ new Date().getFullYear() }}</p>
              <NuxtLink
                href="https://www.dku.dk/forside"
                target="_blank"
                rel="noopener noreferrer"
                class="flex items-center gap-1 pt-4 hover:text-white transition-colors duration-150"
              >
                <NuxtImg
                  src="/images/logo-dku.png"
                  alt="DKU logo"
                  :width="36"
                  :height="36"
                  loading="lazy"
                  class="-ml-1 size-6 md:size-9"
                />
                <span class="text-sm">Indehaveren er medlem af DKU</span>
              </NuxtLink>
            </div>
          </div>

          <!-- Contact -->
          <div class="flex flex-col items-center md:items-start">
            <h4
              class="font-semibold mb-4"
            >
              Kontakt
            </h4>
            <ul
              v-if="entrance?.value?.contacts"
              class="space-y-3"
            >
              <li
                v-for="(contact, i) in entrance.value.contacts"
                :key="i"
                class="flex items-center justify-between gap-4"
              >
                <div>
                  <p
                    v-if="contact.heading"
                    class="text-sm text-neutral-400"
                  >
                    {{ contact.heading }}
                  </p>
                  <p>{{ contact.content }}</p>
                </div>
                <NuxtLink
                  :href="contact.href"
                  target="_blank"
                  rel="noopener noreferrer"
                  :aria-label="contact.heading || contact.content"
                  class="text-neutral-400 hover:text-white transition-colors duration-150"
                >
                  <UIcon
                    :name="mapIcon(contact.icon?.content)"
                    class="size-5"
                  />
                </NuxtLink>
              </li>
            </ul>
          </div>

          <!-- Map -->
          <div class="flex flex-col items-center md:items-start">
            <h4
              class="font-semibold mb-6"
            >
              Find os her
            </h4>
            <LazyVisibleMap
              v-if="location?.value?.address?.google_maps_embed"
              :src="location.value.address.google_maps_embed"
              class="min-h-[250px] w-full rounded-xl"
              hydrate-on-visible
            />
          </div>
        </div>
      </div>
    </footer>
  </div>
</template>
